<script>
import { formatCurrency } from "@/helpers/formatters";

/**
 * Right-aligned totals stack. Pass `rows` as `[{label, value, bold?, accent?, divider?}]`.
 * The "Grand Total" row is typically marked `bold: true, accent: true`.
 *
 * `value` may be a number (formatted via formatCurrency, en-IN locale) or a
 * pre-formatted string.
 */
export default {
  name: "PrintTotalsBox",
  props: {
    rows: { type: Array, default: () => [] },
    width: { type: String, default: "70mm" },
  },
  methods: {
    formatVal(value) {
      if (typeof value === "number") return formatCurrency(value);
      return value;
    },
  },
};
</script>

<template>
  <div class="print-totals-box keep-together" :style="{ width }">
    <div
      v-for="(row, i) in rows"
      :key="i"
      class="print-totals-row"
      :class="{
        bold: row.bold,
        accent: row.accent,
        divider: row.divider,
      }"
    >
      <div class="print-totals-label">{{ row.label }}</div>
      <div class="print-totals-value">{{ formatVal(row.value) }}</div>
    </div>
  </div>
</template>

<style scoped>
.print-totals-box {
  margin-left: auto;
  font-size: var(--print-fs-sm);
  border-top: 0.5pt solid var(--print-border-strong);
  border-bottom: 0.5pt solid var(--print-border-strong);
}

.print-totals-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.6mm 2mm;
  gap: 4mm;
  border-bottom: 0.25pt solid var(--print-border);
}

.print-totals-row:last-child {
  border-bottom: 0;
}

.print-totals-row.divider {
  border-top: 0.5pt solid var(--print-border-strong);
}

.print-totals-row.bold {
  font-weight: 700;
  font-size: var(--print-fs-base);
  padding: 1mm 2mm;
}

.print-totals-row.accent {
  background: var(--print-accent-soft);
  color: var(--print-accent);
}

.print-totals-label {
  color: var(--print-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: var(--print-fs-xs);
}

.print-totals-row.bold .print-totals-label {
  color: inherit;
  font-size: var(--print-fs-sm);
  letter-spacing: 0.04em;
}

.print-totals-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  white-space: nowrap;
}
</style>
